import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  OnDestroy,
  signal
} from '@angular/core';
import {PaperChatComponent} from '../paper-chat/paper-chat.component';
import {CloseableDialogComponent} from '../../../../utils/components/closeable-dialog/closeable-dialog.component';
import {MAT_DIALOG_DATA, MatDialogContent, MatDialogRef} from '@angular/material/dialog';
import {Store} from '@ngrx/store';
import {ResearchPaperDTO} from '../../../research/dto/research-paper';
import {
  FindResultMatchesCount,
  NgxExtendedPdfViewerModule,
  NgxExtendedPdfViewerService,
  PDFNotificationService,
  RenderedTextLayerHighlights
} from 'ngx-extended-pdf-viewer';
import {environment} from '../../../../../environments/environment';
import {selectLastChatSources} from '../../../process-info/services/process-info.selectors';
import {MatIconButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {MatTabsModule} from '@angular/material/tabs';
import {
  selectEmbeddingError,
  selectEmbeddingLoading,
  selectPaperHasEmbeddingCreated
} from '../../service/embedding.selectors';
import {checkAndCreatePaperEmbedding} from '../../service/embedding.actions';
import {JsonPipe} from '@angular/common';
import {ErrorCardComponent} from '../../../../utils/components/error-card/error-card.component';
import {LoadingCardComponent} from '../../../../utils/components/loading-card/loading-card.component';
import {clearChatSourcesMessage} from '../../../process-info/services/process-info.actions';
import {toSignal} from '@angular/core/rxjs-interop';
import {map} from 'rxjs/operators';

interface PaperChatDialogData {
  paper: ResearchPaperDTO;
}

@Component({
  selector: 'app-paper-chat-dialog',
  imports: [
    PaperChatComponent,
    CloseableDialogComponent,
    MatDialogContent,
    NgxExtendedPdfViewerModule,
    MatIconButton,
    MatIcon,
    MatProgressSpinner,
    MatTabsModule,
    ErrorCardComponent,
    JsonPipe,
    LoadingCardComponent,
  ],
  templateUrl: './paper-chat-dialog.component.html',
  styleUrl: './paper-chat-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaperChatDialogComponent implements AfterViewInit, OnDestroy {
  private readonly ngxExtendedPdfViewerService: NgxExtendedPdfViewerService = inject(NgxExtendedPdfViewerService);
  private readonly pdfNotificationService: PDFNotificationService = inject(PDFNotificationService);
  private readonly dialogRef: MatDialogRef<PaperChatDialogComponent> = inject(MatDialogRef);
  private readonly store: Store = inject(Store);
  private readonly breakpointObserver: BreakpointObserver = inject(BreakpointObserver);

  readonly data: PaperChatDialogData = inject<PaperChatDialogData>(MAT_DIALOG_DATA);

  firstSources: boolean = false;
  currentMatchNumber: number = 0;
  totalMatches: number = 0;

  readonly viewInit = signal(false);
  readonly isSmallScreen = toSignal(
    this.breakpointObserver.observe([Breakpoints.Handset]).pipe(map(result => result.matches)),
    {initialValue: false}
  );

  readonly embeddingCheckLoading = this.store.selectSignal(selectEmbeddingLoading);
  readonly embeddingCheckError = this.store.selectSignal(selectEmbeddingError);
  readonly hasEmbedding = this.store.selectSignal(selectPaperHasEmbeddingCreated(this.data.paper.id));
  readonly lastSources = this.store.selectSignal(selectLastChatSources());

  constructor() {
    this.store.dispatch(checkAndCreatePaperEmbedding({paperId: this.data.paper.id}));

    effect(() => {
      const lastSources = this.lastSources();
      if (!this.firstSources && lastSources) {
        this.firstSources = true;
      }
      if (this.firstSources) {
        this.find(lastSources);
      }
    });
  }

  ngAfterViewInit() {
    this.viewInit.set(true);
  }

  ngOnDestroy() {
    this.store.dispatch(clearChatSourcesMessage());

  }

  pdfColorEffect$ = effect(() => {
    this.pdfNotificationService.onPDFJSInitSignal()?.eventBus?.on('renderedtextlayerhighlights', (event: RenderedTextLayerHighlights) => {
      event.highlights.forEach((highlight) => {
        highlight.style.backgroundColor = "rgba(255, 231, 166, 0.3)";
      });
    });
  });

  toggleMaximize(isMaximized: boolean): void {
    if (isMaximized) {
      this.dialogRef.updateSize('100vw', '100vh');
    } else {
      this.dialogRef.updateSize('100vw', '80vh');
    }
  }

  getPDFUrl(): string | undefined {
    if (!this.data.paper) {
      return undefined;
    }
    if (!this.data.paper.hasDownloadedPdf) {
      return undefined;
    }
    const url = this.data.paper.pdfUrl;
    if (!url) {
      return undefined;
    }
    if (url.startsWith('http')) {
      return url;
    }
    return environment.apiBaseUrl + url;
  }

  pdfLoadingFailed(error: any) {
    debugger;
  }

  find(lastSources: string | null) {
    //TODO
    if (this.isSmallScreen()) {
      return;
    }
    let searchtext = lastSources ? lastSources.split('\n') : [];
    searchtext = searchtext
      .map(s => s.trim())
      .filter(s => s.length > 50
      )
    this.ngxExtendedPdfViewerService
      .find(searchtext, {
        highlightAll: true,
        findMultiple: true,
        regexp: false,
      });
  }

  public findNext(): void {
    this.ngxExtendedPdfViewerService.findNext();
  }

  public findPrevious(): void {
    this.ngxExtendedPdfViewerService.findPrevious();
  }

  public updateFindMatchesCount(result: FindResultMatchesCount) {
    this.currentMatchNumber = result.current;
    this.totalMatches = result.total;
  }

}
